import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotifyPrefs } from '@capsico/contracts';
import { errorMessage, errorStatus, errorRef } from '../api/client';
import { fetchNotifyPrefs, saveNotifyPrefs } from '../api/notify';
import { showToast } from './useToast';

export const notifyPrefsKey = ['notify-prefs'] as const;

/** Switches the customer can change. Orders / support / account messages are always on. */
export type PrefSwitch = 'offers' | 'reminders' | 'push' | 'sms' | 'whatsapp';

/**
 * Notification preferences (W18). Each switch saves at once (optimistic, rolled back on failure)
 * with the version it was read at, so two phones cannot silently overwrite each other. Nothing is
 * typed here, so there is no draft to keep.
 */
export function useNotifyPrefs(enabled: boolean, messages: { saved: string; failed: string }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: notifyPrefsKey,
    queryFn: fetchNotifyPrefs,
    enabled,
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: (next: NotifyPrefs) =>
      saveNotifyPrefs({
        offers: next.offers,
        reminders: next.reminders,
        push: next.push,
        sms: next.sms,
        whatsapp: next.whatsapp,
        expectedVersion: next.version > 0 ? next.version : null,
      }),
    onMutate: async (next) => {
      await client.cancelQueries({ queryKey: notifyPrefsKey });
      const before = client.getQueryData<NotifyPrefs>(notifyPrefsKey);
      client.setQueryData<NotifyPrefs>(notifyPrefsKey, next);
      return { before };
    },
    onError: (error, _next, context) => {
      if (context?.before) client.setQueryData(notifyPrefsKey, context.before);
      showToast(errorMessage(error, messages.failed), { tone: 'negative', ref: errorRef(error) });
      if (errorStatus(error) === 409) void client.invalidateQueries({ queryKey: notifyPrefsKey });
    },
    onSuccess: (saved) => {
      client.setQueryData(notifyPrefsKey, saved);
      showToast(messages.saved, { tone: 'positive' });
    },
  });

  const prefs = query.data ?? null;
  return {
    loading: query.isLoading,
    failed: query.isError && !prefs,
    error: query.error ? errorMessage(query.error) : null,
    retry: () => void query.refetch(),
    prefs,
    saving: save.isPending,
    set: (field: PrefSwitch, value: boolean) => {
      if (!prefs || save.isPending) return;
      save.mutate({ ...prefs, [field]: value });
    },
  };
}
